/**
 * The moderator's actions on one ticket.
 *
 * Moderators work tickets one-handed on a phone, so below `sm` this is a bar pinned to the bottom of
 * the scrolling page (sticky inside <main>, which is the scroll container, with the safe-area inset
 * under it): the next step as one big button, everything else in a labelled "More" menu, and "Next"
 * to step through the queue. Destructive actions only ever live in the menu there, so the thumb
 * cannot land on Reject by accident. At `sm` and up it is an ordinary wrapping row of buttons.
 */

import { useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { MoreVert, NavigateNext } from '@mui/icons-material'

export interface TicketBarAction {
  key: string
  label: string
  icon?: ReactNode
  onClick: () => void
  color?: 'primary' | 'success' | 'info' | 'warning' | 'error'
  /** One line under the label in the phone menu. */
  hint?: string
}

interface TicketActionBarProps {
  /** In order of preference: the first non-destructive one is the big button. */
  actions: TicketBarAction[]
  busy?: boolean
  /** Next ticket in the list the moderator came from; omitted when there is none. */
  onNext?: () => void
  nextLabel?: string
  /**
   * `inline` draws the desktop row (nothing on a phone); `bottom` draws the phone bar (nothing at
   * sm+). The bottom one must be a direct child of the page's content box: sticky only sticks
   * within its parent, so wrapping it would pin it to nothing.
   */
  placement: 'inline' | 'bottom'
}

export function TicketActionBar({ actions, busy = false, onNext, nextLabel = 'Next', placement }: TicketActionBarProps) {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  if (actions.length === 0 && !onNext) return null
  if ((placement === 'bottom') !== isXs) return null

  const primary = actions.find((a) => a.color !== 'error')
  const rest = actions.filter((a) => a !== primary)

  if (placement === 'inline') {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {actions.map((a) => (
          <Button
            key={a.key}
            variant={a === primary ? 'contained' : 'outlined'}
            color={a.color ?? 'primary'}
            startIcon={a.icon}
            onClick={a.onClick}
            disabled={busy}
          >
            {a.label}
          </Button>
        ))}
        {busy && <CircularProgress size={20} />}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar - 1,
        // Bleed to the screen edges past PageShell's phone gutter.
        mx: -1.5,
        mb: -2,
        px: 1.5,
        pt: 1,
        pb: 'calc(8px + env(safe-area-inset-bottom))',
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {primary && (
        <Button
          variant="contained"
          size="large"
          color={primary.color ?? 'primary'}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : primary.icon}
          onClick={primary.onClick}
          disabled={busy}
          sx={{ flex: 1, minHeight: 48, minWidth: 0 }}
        >
          {primary.label}
        </Button>
      )}
      {rest.length > 0 && (
        <>
          <Button
            variant="outlined"
            startIcon={<MoreVert />}
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={busy}
            aria-haspopup="menu"
            sx={{ minHeight: 48, flex: primary ? '0 0 auto' : 1 }}
          >
            More
          </Button>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            {rest.map((a) => (
              <MenuItem
                key={a.key}
                onClick={() => {
                  setAnchor(null)
                  a.onClick()
                }}
                sx={{ minHeight: 48, color: a.color && a.color !== 'primary' ? `${a.color}.main` : undefined }}
              >
                {a.icon && <ListItemIcon sx={{ color: 'inherit' }}>{a.icon}</ListItemIcon>}
                <ListItemText primary={a.label} secondary={a.hint} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {onNext && (
        <Button
          variant={primary || rest.length > 0 ? 'text' : 'contained'}
          endIcon={<NavigateNext />}
          onClick={onNext}
          sx={{ minHeight: 48, flex: primary || rest.length > 0 ? '0 0 auto' : 1 }}
        >
          {nextLabel}
        </Button>
      )}
    </Box>
  )
}
